"use client";

/**
 * ArtifactTopBar — shared sticky top bar for artifact pages (niche/plan/brand).
 *
 * Solves three concerns in one place so each artifact page no longer ships
 * its own copy that drifts apart over time:
 *
 *   1. Flush placement under the global navbar.
 *      Sticky `top-[var(--layout-navbar-h)]`. The navbar publishes its real
 *      rendered height to that custom property via ResizeObserver in
 *      NavbarShell, so the bar sits flush at any viewport / font-loading
 *      state — no 12px breathing gap, no sliver after translate-y-full.
 *
 *   2. Mobile autohide.
 *      Scroll-down past 80px tucks the bar by its full height; scroll-up
 *      reveals it. Threshold 8px dampens iOS rubber-band dithering. Desktop
 *      forces `lg:translate-y-0` so the bar always renders when vertical
 *      space isn't scarce.
 *
 *   3. Pin-line for descendant sticky charts.
 *      Publishes `--waterfall-pin-top` on <html> based on the hidden state
 *      (= navbar height when hidden, = navbar + back-bar height when shown).
 *      MathScene's WaterfallScrollPin and any future chart that pins under
 *      the chrome consume this var directly — they don't need to know about
 *      the autohide state.
 *
 * Slots:
 *   leftSlot  — typically <BackToStartup .../> on plan/brand or a plain
 *               "← Back" button on niche.
 *   rightSlot — typically <VisibilityToggle .../> when isOwner, or any
 *               extra meta (candidate id label etc.).
 *
 * Why slots over typed `back`/`visibility` props: the three pages already
 * use different left controls (BackToStartup link vs onBack callback button),
 * and forcing them through one prop shape would require adapter logic in
 * every caller. Slots keep this component's surface tiny and let callers
 * compose freely.
 */

import { useEffect, useRef, useState, type ReactNode } from "react";
import { cn } from "@/lib/utils";

interface ArtifactTopBarProps {
  /** Left-aligned content. Required — typically the back affordance. */
  leftSlot: ReactNode;
  /** Right-aligned content. Optional — typically VisibilityToggle. */
  rightSlot?: ReactNode;
  /** When true, desktop follows the same scroll-hide behavior as mobile. */
  desktopAutoHide?: boolean;
  /** Extra classes on the outer sticky wrapper (rare — most callers omit). */
  className?: string;
}

export function ArtifactTopBar({
  leftSlot,
  rightSlot,
  desktopAutoHide = false,
  className,
}: ArtifactTopBarProps) {
  const [hidden, setHidden] = useState(false);
  const barRef = useRef<HTMLDivElement>(null);

  // Mobile autohide. rAF-throttled so it doesn't compete with framer-motion
  // scroll-driven subscriptions on the same axis. 8px threshold dampens iOS
  // rubber-band dithering at the top edge. Below 80px always show — first
  // viewport must always expose the back affordance.
  useEffect(() => {
    if (typeof window === "undefined") return;
    let lastY = window.scrollY;
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        const y = window.scrollY;
        const delta = y - lastY;
        if (Math.abs(delta) >= 8) {
          if (y < 80) setHidden(false);
          else if (delta > 0) setHidden(true);
          else setHidden(false);
          lastY = y;
        }
        ticking = false;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Publish actual rendered back-bar height to `--layout-back-bar-h` on
  // <html>. The 60px in globals.css is an SSR fallback; real height drifts
  // by ~2px per viewport / font-loading state, leaving a visible seam between
  // the bar and any sticky chrome pinned underneath. ResizeObserver follows
  // the live size, so descendants sit flush.
  useEffect(() => {
    const bar = barRef.current;
    if (!bar || typeof document === "undefined") return;
    const update = () => {
      const h = bar.getBoundingClientRect().height;
      if (h > 0) {
        document.documentElement.style.setProperty(
          "--layout-back-bar-h",
          `${h}px`,
        );
      }
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(bar);
    return () => {
      ro.disconnect();
      document.documentElement.style.removeProperty("--layout-back-bar-h");
    };
  }, []);

  // Publish `--waterfall-pin-top` on <html> so any descendant pinned chart
  // can reference it without prop-drilling. Cleared on unmount so leaving
  // an artifact page doesn't leave the var leaking onto unrelated routes.
  useEffect(() => {
    if (typeof document === "undefined") return;
    const root = document.documentElement;
    root.style.setProperty(
      "--waterfall-pin-top",
      hidden
        ? "var(--layout-navbar-h)"
        : "calc(var(--layout-navbar-h) + var(--layout-back-bar-h))",
    );
    return () => {
      root.style.removeProperty("--waterfall-pin-top");
    };
  }, [hidden]);

  return (
    <div
      ref={barRef}
      className={cn(
        "sticky top-[var(--layout-navbar-h)] z-30 bg-surface/70 backdrop-blur-md",
        "transition-transform duration-200 ease-out",
        !desktopAutoHide && "lg:translate-y-0",
        hidden ? "-translate-y-full" : "translate-y-0",
        className,
      )}
    >
      <div className="relative mx-auto flex w-full max-w-7xl items-center justify-between gap-3 px-4 py-3 lg:px-8">
        <div className="flex min-w-0 items-center gap-3">{leftSlot}</div>
        {rightSlot && (
          <div className="flex shrink-0 items-center gap-3">{rightSlot}</div>
        )}
      </div>
    </div>
  );
}
